/**
 * use-visible-nav.ts: modelo do nav visível (RBAC ∩ config-admin).
 *
 * Extrai a filtragem que vivia inline em `AppSidebar.tsx` para um hook puro de
 * MODELO. A `AppSidebar` (Parte 7) passa a só APRESENTAR o resultado
 * (`VisibleNavGroup[]`), sem lógica de RBAC/config, separando "modelo do nav
 * visível" de "apresentação" (CONFIGURACOES-MENU-DESENHO §4.1).
 *
 * Regra (§4.2/§4.3): menu final = RBAC ∩ config-admin. Interseção ESTRITA — o que
 * QUALQUER um dos dois lados nega, some. Nunca união.
 *   - Grupo oculto se `config.segmentosDesativados.includes(g.id)`.
 *   - Item mantido apenas se TODAS forem verdadeiras:
 *       (a) `hasMinRole(role, item.minRole)` — o papel permite;
 *       (b) admin bypassa `allowedModules`; demais checam
 *           `allowedModules.length === 0 || allowedModules.includes(item.id)`
 *           (fallback atual preservado: claim vazio libera o que o role permite);
 *       (c) `!config.modulosDesativados.includes(item.id)` — a config vale para
 *           TODOS, inclusive admin (para o admin ver o menu como a equipe vê).
 *   - Grupo sem item visível não renderiza (filtra grupos vazios ao final).
 *
 * INVARIANTE anti-lockout (§4.3): o item `configuracoes` e o segmento
 * `configuracao` NUNCA são removidos pela config-admin — senão o admin desligaria
 * a própria porta de reativação e ficaria travado. O RBAC ainda vale (ambos têm
 * alcance de admin: `minRole: "admin"` no item e no restante do segmento), então
 * só o admin os enxerga; mas a config não os oculta mesmo que apareçam nas
 * deny-lists.
 */

import { useMemo } from "react";
import { useOrbUser } from "@/features/auth/AuthContext";
import { useMenuConfig } from "@/features/configuracoes/menu-config-context";
import type { OrbRole } from "@/features/auth/auth.types";
import { NAV_GROUPS, type NavGroupId, type NavItem } from "./nav-model";

// ---------------------------------------------------------------------------
// RBAC: ranking de papéis (movido do AppSidebar para reuso pela Parte 7)
// ---------------------------------------------------------------------------

const ROLE_RANK: Record<OrbRole, number> = {
  viewer: 0,
  financeiro: 1,
  admin: 2,
};

/** True se `userRole` tem alcance igual ou superior a `minRole`. */
export function hasMinRole(userRole: OrbRole, minRole: OrbRole): boolean {
  return ROLE_RANK[userRole] >= ROLE_RANK[minRole];
}

// ---------------------------------------------------------------------------
// Invariante anti-lockout: item e segmento protegidos (§4.3)
// ---------------------------------------------------------------------------

/** Item que a config-admin nunca oculta (porta de reativação do próprio admin). */
const ITEM_PROTEGIDO = "configuracoes";
/** Segmento que a config-admin nunca desliga. */
const SEGMENTO_PROTEGIDO: NavGroupId = "configuracao";

// ---------------------------------------------------------------------------
// Modelo do nav visível
// ---------------------------------------------------------------------------

/** Grupo do menu já filtrado para o usuário e a config vigentes. */
export interface VisibleNavGroup {
  id: NavGroupId;
  label: string;
  items: NavItem[];
}

/**
 * Computa o nav visível cruzando RBAC (claims do usuário) com a config-admin do
 * tenant. Memoizado por role, allowedModules e as duas deny-lists da config, para
 * recomputar só quando algo relevante muda (a sidebar reprojeta ao vivo quando o
 * admin salva). Consome `useOrbUser` + `useMenuConfig`; não toca em apresentação.
 */
export function useVisibleNav(): VisibleNavGroup[] {
  const orbUser = useOrbUser();
  const { config } = useMenuConfig();

  const userRole: OrbRole = orbUser?.claims.role ?? "viewer";
  // Referência estável do claim (o `?? []` fica DENTRO do memo para não gerar um
  // array novo a cada render e disparar o memo à toa).
  const rawAllowedModules = orbUser?.claims.allowedModules;
  const { segmentosDesativados, modulosDesativados } = config;

  return useMemo<VisibleNavGroup[]>(() => {
    const allowedModules = rawAllowedModules ?? [];
    return NAV_GROUPS
      // Segmento oculto se desativado — EXCETO o protegido (anti-lockout).
      .filter(
        (group) =>
          group.id === SEGMENTO_PROTEGIDO ||
          !segmentosDesativados.includes(group.id),
      )
      .map((group) => ({
        id: group.id,
        label: group.label,
        items: group.items.filter((item) => {
          // (a) o papel permite.
          if (!hasMinRole(userRole, item.minRole)) return false;
          // (b) admin bypassa allowedModules; demais checam o claim
          //     (vazio => libera o que o role permite; fallback atual).
          if (userRole !== "admin") {
            if (
              allowedModules.length > 0 &&
              !allowedModules.includes(item.id)
            ) {
              return false;
            }
          }
          // (c) config-admin: vale para TODOS, inclusive admin — EXCETO o item
          //     protegido (anti-lockout), que passa mesmo se estiver na deny-list.
          if (
            item.id !== ITEM_PROTEGIDO &&
            modulosDesativados.includes(item.id)
          ) {
            return false;
          }
          return true;
        }),
      }))
      // Grupo sem item visível não renderiza.
      .filter((group) => group.items.length > 0);
  }, [userRole, rawAllowedModules, segmentosDesativados, modulosDesativados]);
}
